"use client"

// Multi-select picker for Product Families. Used by Focus Product on
// `?page=shopify-products` when the «Group by Product Family» toggle is ON
// — replaces the comma-separated text input that required tenants to type
// raw family_keys. Reuses `useProductFamilies` (DRY) so the family list
// + search shares the same auth-scoped, cached fetch as the Economics
// picker, COGS filter, and ProductMultiSelect's families section.
//
// Emits family_key[] (not the full ProductFamilyLight) — matches the
// existing `focusFamilyKeys` prop contract on FocusProductView.

import { useEffect, useMemo, useRef, useState } from "react"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Loader2, Layers, X, Search, Settings } from "lucide-react"
import { useProductFamilies } from "@/hooks/use-product-families"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"



interface FamilyMultiSelectProps {
  integrationId: string
  /** family_key[] currently selected. */
  selected: string[]
  onSelectionChange: (familyKeys: string[]) => void
  maxSelect?: number
  placeholder?: string
}

export function FamilyMultiSelect({
  integrationId,
  selected,
  onSelectionChange,
  maxSelect = 10,
  placeholder = "Search families…",
}: FamilyMultiSelectProps) {

  const tUi = useTranslations("Shopify.ShopifyCrossSellFamilyMultiSelect")

  const [search, setSearch] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // All-time mode (`period: null`) — Focus Product is period-scoped at the
  // analysis level (the parent passes monthStartUtc/monthEndUtc to the
  // analysis fetch); the family list itself is catalog-membership, not
  // period-membership. Same posture as COGS.
  const { families, isLoading } = useProductFamilies({
    integrationId,
    period: null,
  })

  const selectedSet = useMemo(() => new Set(selected), [selected])

  // Search filter — name OR family_key, case-insensitive.
  const filteredFamilies = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (q.length === 0) return families
    return families.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.family_key.toLowerCase().includes(q),
    )
  }, [families, search])

  const remaining = maxSelect - selected.length
  const canAddMore = remaining > 0

  const toggle = (familyKey: string) => {
    if (selectedSet.has(familyKey)) {
      onSelectionChange(selected.filter((k) => k !== familyKey))
      return
    }
    if (!canAddMore) return
    onSelectionChange([...selected, familyKey])
  }

  // Close dropdown on click outside.
  useEffect(() => {
    if (!showDropdown) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [showDropdown])

  // Selected chips render uses the family list to resolve display name from
  // family_key (DRY). Family rule may be deleted between mount and render —
  // fall back to family_key as label.
  const selectedChips = useMemo(() => {
    const byKey = new Map(families.map((f) => [f.family_key, f]))
    return selected.map((key) => byKey.get(key) ?? { family_key: key, name: key, id: key, product_count: 0 })
  }, [selected, families])

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips — clickable X to remove. */}
      {selectedChips.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selectedChips.map((f) => (
            <Badge key={f.family_key} variant="secondary" className="gap-1.5 pr-1">
              <Layers className="h-3 w-3" />
              <span className="font-mono text-xs">{f.family_key}</span>
              <button
                type="button"
                onClick={() => toggle(f.family_key)}
                className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                aria-label={tUi("removeValue0", { value0: String(f.name) })}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input. */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setShowDropdown(true)
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="pl-8"
        />
        {isLoading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown list. */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-80 overflow-y-auto">
          <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b sticky top-0 bg-popover flex items-center gap-1.5">
            <Layers className="h-3 w-3" />
            <span><span key={tUi("productFamilies")}>{tUi("productFamilies")}</span></span>
          </div>
          {!isLoading && filteredFamilies.length === 0 && families.length === 0 ? (
            <a
              href="?page=shopify-bundle-rules"
              className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:bg-accent"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Settings className="h-3 w-3" />
              <span><span key={tUi("noFamiliesYetOpenProductFamiliesToDefine")}>{tUi("noFamiliesYetOpenProductFamiliesToDefine")}</span></span>
            </a>
          ) : !isLoading && filteredFamilies.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              <span><span key={tUi("noFamiliesMatch")}>{tUi("noFamiliesMatch")}</span></span>{search}<span>».</span>
            </div>
          ) : (
            filteredFamilies.map((family) => {
              const isSelected = selectedSet.has(family.family_key)
              const isDisabled = !isSelected && !canAddMore
              return (
                <div
                  key={family.id}
                  className={cn(
                    "w-full text-left px-2 py-1.5 flex items-center gap-2",
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-accent cursor-pointer",
                    isSelected && "bg-accent/50",
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (isDisabled) return
                    toggle(family.family_key)
                  }}
                >
                  <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{family.name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                      {family.family_key}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                    {family.product_count}
                  </Badge>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
